import { useState } from 'react'
import { Lightbulb, PackagePlus, Wrench, CheckCircle2, XCircle } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import EmptyState from '@/components/common/EmptyState'

/**
 * LO QUE LA MIGRACIÓN DESCUBRE.
 *
 * Meter los datos de un cliente es la única vez que se ve todo lo que ese negocio hace.
 * Cada columna que no encaja, cada hoja que no corresponde a nada y cada tope desbordado es
 * información comercial que hasta ahora se tiraba: el asesor la veía, la resolvía a mano y
 * nadie más se enteraba.
 *
 * La regla que gobierna la pantalla: NADA se hace solo. Todo hallazgo nace pendiente y
 * termina en una decisión con nombre y fecha — incluido "no, esto no se hace", que en el
 * acta vale tanto como el sí.
 */

const CAJONES = {
  'ampliar-contrato': {
    titulo: 'Necesita algo que ya existe',
    ayuda: 'Está en el catálogo y no está vendido. Se propone una addenda, en borrador y con su precio.',
    icono: <PackagePlus size={18} />,
    variante: 'warning',
  },
  'requiere-desarrollo': {
    titulo: 'Necesita algo que todavía no existe',
    ayuda: 'Se eleva como solicitud de desarrollo, con la evidencia adjunta.',
    icono: <Wrench size={18} />,
    variante: 'danger',
  },
  'no-se-migra': {
    titulo: 'No se migra, y se acepta',
    ayuda: 'Queda por escrito en el acta. Es lo que evita la llamada del contador tres meses después.',
    icono: <Lightbulb size={18} />,
    variante: 'secondary',
  },
}

export default function Requerimientos({ informe, cargando, onDetectar, onDecidir }) {
  const [notas, setNotas] = useState({})
  const hallazgos = informe?.hallazgos || []

  const porCajon = Object.keys(CAJONES)
    .map((cajon) => ({ cajon, ...CAJONES[cajon], items: hallazgos.filter((h) => h.cajon === cajon) }))
    .filter((g) => g.items.length > 0)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={onDetectar} disabled={cargando}>
          {cargando ? 'Revisando…' : 'Revisar qué necesita este cliente'}
        </Button>
        {informe && (
          <span className="text-caption text-neutral-500 dark:text-neutral-400">
            {hallazgos.length} hallazgo(s) · {hallazgos.filter((h) => h.decision === 'pendiente').length} sin decidir
          </span>
        )}
      </div>

      {informe && hallazgos.length === 0 && (
        <EmptyState
          icono={<CheckCircle2 size={28} />}
          titulo="Todo lo que trae cabe en lo que compró"
          descripcion="Ni módulos que falten, ni topes desbordados, ni campos sin dónde entrar."
        />
      )}

      {porCajon.map((grupo) => (
        <section key={grupo.cajon} className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-neutral-400 dark:text-neutral-500">{grupo.icono}</span>
            <h3 className="text-body font-medium text-neutral-900 dark:text-neutral-50">{grupo.titulo}</h3>
            <Badge variant={grupo.variante}>{grupo.items.length}</Badge>
          </div>
          <p className="text-caption text-neutral-500 dark:text-neutral-400">{grupo.ayuda}</p>

          <ul className="space-y-2">
            {grupo.items.map((h) => (
              <li key={h.id}>
                <Card>
                  <CardContent className="space-y-2 pt-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-neutral-900 dark:text-neutral-50">{h.titulo}</span>
                      {h.decision === 'aceptado' && (
                        <Badge variant="success">
                          <CheckCircle2 size={12} className="mr-1" /> Aceptado
                        </Badge>
                      )}
                      {h.decision === 'descartado' && (
                        <Badge variant="secondary">
                          <XCircle size={12} className="mr-1" /> Descartado
                        </Badge>
                      )}
                    </div>
                    {/* Qué significa para el CLIENTE. Sin esto un hallazgo es una curiosidad. */}
                    <p className="text-body-sm text-neutral-600 dark:text-neutral-400">{h.porque}</p>
                    {h.evidencia?.ejemplos?.length > 0 && (
                      <p className="text-caption text-neutral-500 dark:text-neutral-400">
                        Por ejemplo: {h.evidencia.ejemplos.slice(0, 5).join(' · ')}
                      </p>
                    )}
                    {h.nota && <p className="text-caption italic text-neutral-500 dark:text-neutral-400">“{h.nota}”</p>}

                    {h.decision === 'pendiente' && (
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        <Input
                          className="max-w-xs"
                          placeholder="Nota para el acta (opcional)"
                          value={notas[h.id] || ''}
                          onChange={(e) => setNotas((n) => ({ ...n, [h.id]: e.target.value }))}
                        />
                        <Button size="sm" onClick={() => onDecidir(h.id, 'aceptado', notas[h.id])}>
                          {h.accion?.tipo === 'solicitud-plugin' ? 'Pedir desarrollo' : 'Proponer al cliente'}
                        </Button>
                        {/* Descartar también se registra: el acta tiene que decir qué se vio
                            y se decidió no hacer, o dentro de seis meses nadie sabrá si se
                            pasó por alto. */}
                        <Button variant="ghost" size="sm" onClick={() => onDecidir(h.id, 'descartado', notas[h.id])}>
                          No aplica
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
